import { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import DialogLoading from '../../components/feedback/DialogLoading';
import ErrorMessage from '../../components/ui/ErrorMessage';
import InputInfo from '../../components/ui/InputInfo';
import { useCreateCompany } from '../../hooks/useCompany';
import { addCompanySchema } from '../../schemas/company';
import { useDialogStore } from '../../stores/dialogStore';

const AdminAddCompanyPage = () => {
  const navigate = useNavigate();
  const [loadingDialog, setLoadingDialog] = useState(false);
  const openDialog = useDialogStore((state) => state.openDialog);

  const { mutateAsync: createCompany } = useCreateCompany();

  const formik = useFormik({
    initialValues: {
      code: '',
      companyName: '',
      spendingCriteria: 0,
    },
    validationSchema: addCompanySchema,
    onSubmit: async (values) => {
      try {
        setLoadingDialog(true);

        const dto = {
          code: values.code,
          company_name: values.companyName,
          spending_criteria: values.spendingCriteria,
        };

        const response = await createCompany(dto);

        if (response.Code === '20100') {
          openDialog({
            title: 'สำเร็จ',
            message: 'เพิ่มบริษัทสำเร็จ',
            variant: 'success',
            onCloseCallback: () => {
              navigate('/admin');
            },
          });
        }
      } catch (err) {
        console.error(err);
        openDialog({
          title: 'เกิดข้อผิดพลาด',
          message: 'เพิ่มบริษัทไม่สำเร็จ',
          variant: 'error',
        });
      } finally {
        setLoadingDialog(false);
      }
    },
  });

  return (
    <Box
      sx={{
        width: '100%',
        padding: '6rem 2rem 40px',
        overflow: 'auto',
        position: 'relative',
      }}
    >
      <Typography
        sx={{
          fontSize: '2.5rem',
          fontWeight: '700',
          color: '#002B38',
          textAlign: 'center',
          marginBottom: '40px',
        }}
      >
        เพิ่มบริษัท
      </Typography>
      <Box
        component="form"
        onSubmit={formik.handleSubmit}
        sx={{
          maxWidth: '768px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}
      >
        {/* Code */}
        <Box>
          <InputInfo
            id="code"
            name="code"
            text="รหัสบริษัท"
            placeholder="รหัสบริษัท"
            value={formik.values.code}
            onChange={formik.handleChange}
          />
          {formik.touched.code && formik.errors.code && (
            <ErrorMessage>{formik.errors.code}</ErrorMessage>
          )}
        </Box>

        {/* Company Name */}
        <Box>
          <InputInfo
            id="companyName"
            name="companyName"
            text="ชื่อบริษัท"
            placeholder="ชื่อบริษัท"
            value={formik.values.companyName}
            onChange={formik.handleChange}
          />
          {formik.touched.companyName && formik.errors.companyName && (
            <ErrorMessage>{formik.errors.companyName}</ErrorMessage>
          )}
        </Box>

        {/* Spending Criteria */}
        <Box>
          <InputInfo
            id="spendingCriteria"
            name="spendingCriteria"
            type="number"
            text="ยอดใช้จ่าย"
            placeholder="ยอดใช้จ่าย"
            value={formik.values.spendingCriteria}
            onChange={formik.handleChange}
          />
          {formik.touched.spendingCriteria && formik.errors.spendingCriteria && (
            <ErrorMessage>{formik.errors.spendingCriteria}</ErrorMessage>
          )}
        </Box>

        {/* Buttons */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 0',
            marginTop: '50px',
          }}
        >
          <Button
            type="button"
            sx={{
              width: '250px',
              height: '50px',
              borderRadius: '8px',
              backgroundColor: 'white',
              color: '#004C60',
              fontSize: '1.25rem',

              '&:hover': {
                backgroundColor: 'white',
                color: '#004C60',
                opacity: '.85',
              },
            }}
            onClick={() => navigate('/admin')}
          >
            ยกเลิก
          </Button>
          <Button
            type="submit"
            sx={{
              width: '250px',
              height: '50px',
              borderRadius: '8px',
              backgroundColor: '#E1000C',
              color: 'white',
              fontSize: '1.25rem',

              '&:hover': {
                backgroundColor: '#E1000C',
                color: 'white',
                opacity: '.85',
              },
            }}
          >
            บันทึก
          </Button>
        </Box>
      </Box>

      <DialogLoading open={loadingDialog} message="กำลังเพิ่มบริษัท" />
    </Box>
  );
};

export default AdminAddCompanyPage;
